import { useEffect, useState } from 'react';

import { PnLService } from '@services/pnlService';
import { priceService } from '@services/priceService';
import { volatilityService } from '@services/volatilityService';
import type { LedgerTx, Option, Position } from '@shared/types';

import { useAccountService } from './useAccountService';

export interface UseAccountState {
  balance: number;
  positions: Position[];
  ledger: LedgerTx[];
  accountValue: number;
  portfolioMarketValue: number;
  openPnl: number;
  deposit: (amount: number) => void;
  buyOption: (params: Option) => Position | null;
  getPositionPnLData: (
    position: Position,
  ) => { pnlTotal: number; currentValue: number; totalCost: number } | null;
}

export function useAccount(): UseAccountState {
  const accountService = useAccountService();
  const initial = accountService.getState();
  const [balance, setBalance] = useState<number>(initial.balance);
  const [positions, setPositions] = useState<Position[]>(initial.positions);
  const [ledger, setLedger] = useState<LedgerTx[]>(initial.ledger);

  useEffect(() => {
    accountService.init();
    const unsubscribe = accountService.subscribe(s => {
      setBalance(s.balance);
      setPositions(s.positions);
      setLedger(s.ledger);
    });
    return () => {
      unsubscribe();
    };
  }, []);

  // Calculate portfolio cost basis (sum of purchase prices)
  const portfolioCostBasis = positions.reduce((total, position) => {
    return total + position.purchasePrice * position.quantity;
  }, 0);

  // Calculate openPnl directly using PnLService (not through AccountService)
  const openPnl = (() => {
    try {
      const { totalPnL } = PnLService.calculatePortfolioPnL(
        positions,
        Date.now(),
      );
      return Math.round(totalPnL * 100) / 100;
    } catch (error) {
      console.warn('Error calculating open PnL:', error);
      return 0;
    }
  })();

  // Calculate portfolio current market value (cost basis + PnL)
  const portfolioMarketValue = portfolioCostBasis + openPnl;

  // Calculate total account value (balance + current market value of positions)
  const accountValue = Math.round((balance + portfolioMarketValue) * 100) / 100;

  const deposit = (amount: number) => accountService.deposit(amount);
  const buyOption = (params: Option) => accountService.buyOption(params);
  const getPositionPnLData = (position: Position) => {
    try {
      const spotPrice = priceService.getPrice(position.asset)?.price;
      const volatility = volatilityService.getVolatility(position.asset);

      if (!spotPrice || !volatility || volatility <= 0) {
        return null;
      }

      return PnLService.calculatePositionPnL(
        position,
        spotPrice,
        volatility,
        Date.now(),
      );
    } catch (error) {
      console.warn('Error calculating position PnL:', error);
      return null;
    }
  };

  return {
    balance,
    positions,
    ledger,
    accountValue,
    portfolioMarketValue,
    openPnl,
    deposit,
    buyOption,
    getPositionPnLData,
  };
}
